import { Directionality, UnitType } from 'BMapsSrc/integrations/properties_common';
import { PropApplicability } from 'BMapsSrc/model/ScopedPropertyGroup';
import { deepFreeze } from 'BMapsUtil/js_utils';
import { JobDefinition, JobResultDestinations, JobTypes } from '../JobDefinition';

// On property explanation, from David Koes (https://github.com/gnina/gnina/issues/259)
// * CNN_VS is CNNaffinity*CNN_score
// * CNNaffinity is a binding affinity prediction in pK units (e.g. 9 is 1 nM)
// * CNNscore is a predicted probability that the pose is good (<2A from correct)
// * minimizedAffinity is the Vina score

// gninaProperties are used by gninaScoring, gninaDocking, and libraryScreening jobs.
export const gninaProperties = deepFreeze({
    affinity: {
        metadata: {
            label: 'Affinity (AutoDock Vina)',
            description: 'Predicted binding affinity from AutoDock Vina scoring function',
            unit: 'kcal/mol',
            directionality: Directionality.MINIMIZE,
            defaultVisible: true, // used by gnina docking and library screening results tables.
            propApplicability: PropApplicability.PERPOSE,
        },
    },
    cnn_score: {
        metadata: {
            label: 'Gnina Pose Score',
            description: "Gnina's predicted probability that the pose is within 2Å of the correct binding pose",
            unit: 'Probability',
            directionality: Directionality.MAXIMIZE,
            defaultVisible: true,
            propApplicability: PropApplicability.PERPOSE,
        },
    },
    cnn_affinity: {
        metadata: {
            label: 'Gnina Affinity (CNN)',
            description: "Gnina's prediction of binding affinity in pK units (6 = 1 µM, 9 = 1 nM)",
            unit: 'pK',
            directionality: Directionality.MAXIMIZE,
            defaultVisible: true,
            propApplicability: PropApplicability.PERPOSE,
        },
    },
    cnn_variance: {
        metadata: {
            label: 'Gnina CNN Variance',
            description: "Gnina's report of the variance/uncertainty of the CNN affinity scoring, a measure of confidence in the prediction",
            unit: 'pK Variance',
            unitType: UnitType.GTE_ZERO,
            directionality: Directionality.MINIMIZE,
            propApplicability: PropApplicability.PERPOSE,
        },
    },
    cnn_vs: {
        metadata: {
            label: 'Gnina VS',
            description: "Gnina's virtual screening score (Gnina affinity * Gnina pose score)",
            unit: '(composite)',
            directionality: Directionality.MAXIMIZE,
            defaultVisible: true,
            propApplicability: PropApplicability.PERPOSE,
        },
    },
    sourceSpec: {
        metadata: {
            label: 'Gnina docking source cmpd',
            description: 'Name of the input ligand to gnina docking',
            propApplicability: PropApplicability.PERPOSE,
        },
    },
});

const scopedPropertyMetadata = deepFreeze({
    compound: {
        childGroups: {
            gnina_scoring: {
                metadata: {
                    label: 'Gnina properties',
                    tooltipHeading: 'Gnina properties',
                },
                properties: gninaProperties,
            },
        },
    },
});

/**
 * Gnina scoring job: scores compounds in place (`score_only`) and writes the
 * resulting properties onto the workspace compounds under the `gnina_scoring`
 * scope.
 *
 * @augments JobDefinition
 */
export class GninaScoringJob extends JobDefinition {
    static jobTypeId = JobTypes.GNINA_SCORING;
    static resultDestination = JobResultDestinations.WORKSPACE;
    static jobTypeName = 'Gnina Scoring';
    static jobName = 'Gnina Scoring Job';

    static scopedPropertyMetadata = scopedPropertyMetadata;

    /**
     * Gnina scoring carries no extra request metadata.
     * @override
     * @returns {object}
     */
    getRequestMeta() {
        return {};
    }

    /**
     * Maps the per-compound score results onto the supplied workspace items,
     * setting each score as a property under this job's scope.
     * @override
     * @param {object} _job
     * @param {object|string} results
     * @param {import('../JobDefinition').IntegrateResultsOptions} [options]
     * @returns {Promise<import('../JobDefinition').IntegrateResultsReturn>}
     */
    async integrateResults(_job, results, options) {
        let resultsPacket = results;
        let error;
        try {
            if (!resultsPacket) return { error: 'Empty results' };
            if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
            if (!resultsPacket.jobResults?.scores) return { error: 'Empty results' };
            const { items = [] } = options || {};
            for (let i = 0; i < resultsPacket.jobResults.scores.length; i++) {
                const { name, ...scoreProps } = resultsPacket.jobResults.scores[i];
                const cmpd = items[i];
                if (!cmpd) continue;
                if (name !== cmpd.resSpec && !cmpd.isLigand()) {
                    console.warn(`gnina scores for ${name} don't match expected compound ${cmpd.resSpec} (index ${i})`);
                }
                for (const [propName, value] of Object.entries(scoreProps)) {
                    if (value !== undefined) {
                        cmpd.setProperty(this.jobTypeId, propName, value);
                    }
                }
            }
        } catch (ex) {
            error = `Error loading ${this.jobTypeId} results`;
            console.error(error, ex);
        }
        return { error, results: resultsPacket };
    }
}
